import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { login as apiLogin, logout as apiLogout, refreshToken as apiRefresh, UserProfile, getErrorMessage } from '../api/authApi'
import { authClient } from '../api/authClient'
import { AuthClientError, ClientErrorCode } from '@paperair/auth-client'
import { useLauncherStore } from './launcherStore'

interface AuthState {
  // State
  user: UserProfile | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitializing: boolean
  error: string | null
  mustChangePassword: boolean
  _hasHydrated: boolean

  // Actions
  login: (email: string, password: string) => Promise<boolean>
  loginWithPasskey: (opts?: { useBrowserAutofill?: boolean; signal?: AbortSignal }) => Promise<boolean>
  logout: () => Promise<void>
  refresh: () => Promise<boolean>
  initialize: () => Promise<void>
  clearError: () => void
  clearMustChangePassword: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isInitializing: true,
      error: null,
      mustChangePassword: false,
      _hasHydrated: false,

      // Login action
      login: async (email: string, password: string): Promise<boolean> => {
        set({ isLoading: true, error: null })

        try {
          const response = await apiLogin(email, password)

          set({
            user: response.user,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            mustChangePassword: response.mustChangePassword || false,
          })

          return true
        } catch (error) {
          set({
            isLoading: false,
            error: getErrorMessage(error),
          })
          return false
        }
      },

      // Passkey login action. Shares post-login handling with `login()` — same session
      // shape, same App.tsx routing on `isAuthenticated`/`mustChangePassword`. The
      // `useBrowserAutofill` conditional-UI request can sit pending from page load until the
      // user picks a credential (or never), so it deliberately does not drive `isLoading` —
      // that's reserved for the explicit "Sign in with a passkey" button.
      loginWithPasskey: async (opts = {}): Promise<boolean> => {
        const { useBrowserAutofill = false, signal } = opts

        if (!useBrowserAutofill) {
          set({ isLoading: true, error: null })
        }

        try {
          const response = await authClient.passkeys.login({ useBrowserAutofill, signal })

          set({
            user: response.user,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            mustChangePassword: response.mustChangePassword || false,
          })

          return true
        } catch (error) {
          // The user dismissing the OS prompt (explicit button) or never engaging the
          // autofill dropdown (conditional UI, abandoned when they type a password instead)
          // is expected, silent behavior, not a login failure — no error toast for either.
          const cancelled = error instanceof AuthClientError && error.code === ClientErrorCode.PASSKEY_CANCELLED

          set({
            isLoading: false,
            error: cancelled ? null : useBrowserAutofill ? null : getErrorMessage(error),
          })
          return false
        }
      },

      // Logout action
      logout: async (): Promise<void> => {
        const { accessToken } = get()

        set({ isLoading: true })

        try {
          if (accessToken) {
            await apiLogout()
          }
        } catch {
          // Ignore logout errors - we'll clear local state anyway
        } finally {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            mustChangePassword: false,
          })
          // Clear leftover launcher state so the next login (any account) always
          // sees the app picker instead of inheriting a stale last-opened app.
          useLauncherStore.setState({ apps: [], lastOpenedAppId: null })
        }
      },

      // Refresh token action
      refresh: async (): Promise<boolean> => {
        const { refreshToken } = get()

        if (!refreshToken) {
          return false
        }

        try {
          const response = await apiRefresh(refreshToken)

          set({
            user: response.user,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
          })

          return true
        } catch {
          // Refresh failed - clear auth state
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          })
          return false
        }
      },

      // Initialize auth state on app load
      // Attempts silent refresh if we have a stored refreshToken
      initialize: async (): Promise<void> => {
        // Wait for Zustand persist to hydrate state from localStorage
        // This prevents a race condition where we check refreshToken before it's loaded
        while (!get()._hasHydrated) {
          await new Promise((resolve) => setTimeout(resolve, 10))
        }

        const { refreshToken, isInitializing } = get()

        // Skip if already initialized
        if (!isInitializing) {
          return
        }

        // No refresh token - just mark as initialized
        if (!refreshToken) {
          set({ isInitializing: false })
          return
        }

        // Attempt silent refresh to get new accessToken
        try {
          const response = await apiRefresh(refreshToken)

          set({
            user: response.user,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isInitializing: false,
          })
        } catch {
          // Refresh failed - clear stored refresh token and mark as initialized
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isInitializing: false,
          })
        }
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Clear mustChangePassword flag (after password change)
      clearMustChangePassword: () => set({ mustChangePassword: false }),
    }),
    {
      name: 'auth-admin-storage',
      // Persist refreshToken and user profile to localStorage
      // accessToken stays in memory only (security: reduces XSS exposure)
      // On page load, initialize() will use refreshToken to get new accessToken
      // User profile is cached for instant display, then refreshed from server
      partialize: (state) => ({
        refreshToken: state.refreshToken,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        // Mark hydration complete so initialize() knows it can read persisted state
        if (state) {
          state._hasHydrated = true
        }
      },
    }
  )
)
